/**
 * StaffList — verbatim port of StaffLibrary + StaffGroups (two-tab screen).
 * Route: staff (tab: Staff members) · staff.groups (tab: Groups)
 *
 * DS-convergence pass (2026-08-10): the two hand-rolled <table>s are now the
 * shared jrni-ui `Table` (rows gain keyboard reach + a focus/hover treatment
 * they never had), the local `FilterDropdown` fork is the shared `FilterSelect`
 * it was promoted from, the status pill rows are `ChipToggleGroup`, the staff
 * glyph is `Avatar`, and both tabs route their read through `LoadState` so a
 * failed list read can no longer render as "no staff".
 */
import { useState, type CSSProperties, type MouseEvent, type ReactNode } from "react";
import { Avatar, Banner, Btn, Card, ChipToggleGroup, ConfirmDialog, Divider, FilterSelect, Icon, Input, Pill, Skeleton, T, Table, type TableColumn } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { FilterBar, EmptyState, ExplainerFooter, LoadState, RowActions } from "../../shell/composites";
import { useViewMode, ViewToggle, CardActionBar, CARD_GRID } from "../../shell/list-composites";
import { StatTile } from "../../shell/composites";
import { can } from "../../shell/access";
import { toast } from "../../shell/toast";
import { useNav } from "../../shell/router";
import { useStaff, type StaffMember, type StaffGroup } from "./useStaff";
import { RegistrationRequests } from "./RegistrationRequests";
import { SVC_CHANNEL, SVC_CHANNELS } from "../../lib/svc-editor-primitives";
import { moduleKnownAbsent, useTenantModules } from "../../lib/tenant-modules";
import { useT, t as i18nT, formatICU } from "../../lib/i18n";
import { useIsMobile } from "../../shell/useResponsive";

/* ── Constants ─────────────────────────────────────────────────────────── */
const STAFF_STATUSES: Record<string, { label: string; labelKey: string; tone: "success" | "neutral" | "warn" }> = {
  active:     { label: "Active", labelKey: "common.status.active", tone: "success" },
  inactive:   { label: "Inactive", labelKey: "common.status.inactive", tone: "neutral" },
  "on-leave": { label: "On leave", labelKey: "staff.list.onLeave", tone: "warn" },
};

/* ── Helpers ────────────────────────────────────────────────────────────── */
export function staffName(m: StaffMember) { return [m.firstName, m.lastName].filter(Boolean).join(" ") || m.email || m.id; }
export function staffInitials(m: StaffMember) {
  const f = m.firstName?.[0] ?? ""; const l = m.lastName?.[0] ?? "";
  return (f + l).toUpperCase() || (m.email?.[0] ?? "?").toUpperCase();
}

function StaffStatusPill({ status }: { status: string }) {
  const t = useT();
  const s = STAFF_STATUSES[status] ?? STAFF_STATUSES.active;
  return <Pill tone={s.tone} dot>{t(s.labelKey, s.label)}</Pill>;
}
function GroupStatusPill({ status }: { status: string }) {
  const t = useT();
  return status === "published" ? <Pill tone="success" dot>{t("common.status.published", "Published")}</Pill> : <Pill tone="warn" dot>{t("common.status.draft", "Draft")}</Pill>;
}
/** The staff entity glyph — jrni-ui `Avatar` (rounded-square), which also owns
 *  the broken-image fallback the hand-rolled <img> did not have. */
function StaffAvatar({ m, size = 32 }: { m: StaffMember; size?: number }) {
  return <Avatar shape="square" size={size} src={m.photo} initials={staffInitials(m)} color={m.color ?? T.p1} alt={staffName(m)} />;
}
function ChannelGlyphs({ channels }: { channels?: string[] }) {
  const t = useT();
  if (!channels?.length) return <span style={{ fontSize: T.text.label.fontSize, color: T.soft }}>{t("staff.list.notBookable", "Not bookable")}</span>;
  return <div style={{ display: "flex", gap: T.space[2] }}>{channels.map((id) => { const c = SVC_CHANNEL(id); const G = (Icon as Record<string, (p: { size: number }) => ReactNode>)[c.icon] ?? Icon.calendar; return <span key={id} title={c.label} style={{ display: "inline-flex" }}><G size={15} /></span>; })}</div>;
}
function sectionTabs(nav: ReturnType<typeof useNav>, store: ReturnType<typeof useStaff>) {
  return [
    { id: "staff",  label: i18nT("staff.list.staff", "Staff"),  count: store.members.length, onClick: () => nav.go("staff") },
    { id: "groups", label: i18nT("staff.list.groups", "Groups"), count: store.groups.length,  onClick: () => nav.go("staff.groups") },
  ];
}
/** "N of M" — a right-aligned footer line BELOW the list, never above it (Pat, 2026-10-17:
 *  the count does not belong at the start of a table). Same treatment as Events' ResultCount. */
function FilterCount({ shown, total, testid }: { shown: number; total: number; testid?: string }) {
  const t = useT();
  return (
    <div aria-live="polite" data-testid={testid} style={{ display: "flex", justifyContent: "flex-end", marginTop: 10, fontSize: 12, color: T.soft }}>
      {shown} {t("staff.list.of", "of")}{" "}{total}
    </div>
  );
}
/** Thin rule between filter-bar clusters. Not drawn on a phone, where the bar wraps and a rule would sit
 *  alone at the start of a line (CC 2026-10-18 item 1). */
const FilterRule = () => useIsMobile() ? null : <Divider orientation="vertical" inset={4} style={{ height: 20, alignSelf: "center" }} />;
/** The list area. On a phone the page scrolls as one surface (the shell's main is already the scroller), so
 *  the list does not shrink into the strip left under the header and filters (item 1). */
const listWrap = (mobile: boolean): CSSProperties => mobile
  ? { flexShrink: 0, padding: `0 ${T.space[6]} ${T.space[6]}` }
  : { flex: 1, overflow: "auto", padding: `0 ${T.space[6]} ${T.space[6]}` };

/* ═══ Tab 1: Staff members ══════════════════════════════════════════════ */
/** Neither booking nor queue composed, and KNOWN (CC, 2026-10-03): staff here run and staff events,
 *  so delivery channels, "bookable" and services per group do not apply. Unknown = today's screen. */
function useEventsOnlyStaff(): boolean {
  const mods = useTenantModules();
  return moduleKnownAbsent("booking", mods) && moduleKnownAbsent("queue", mods);
}

export function StaffLibrary() {
  const t = useT();
  const eventsOnly = useEventsOnlyStaff();
  const statusLabel = (status: string) => { const meta = STAFF_STATUSES[status]; return meta ? t(meta.labelKey, meta.label) : status; };
  const nav = useNav();
  const store = useStaff();
  const [view, setView] = useViewMode("staff:view");
  /* Phones get cards, as Queues does (CC 2026-10-18 item 4a). */
  const isMobile = useIsMobile();
  const shownView = isMobile ? "grid" : view;
  /* Before the first read settles there is nothing to count: skeletons, never zeros (item 2). */
  const firstLoad = store.loading && store.members.length === 0;
  const [query, setQuery] = useState("");
  /* "current" (the default) is everyone NOT deactivated (2026-10-08). Deactivating
     is a soft delete: the record and its history stay, with status inactive. The
     old default showed every status, so a record the user had just "removed"
     came straight back on the next read. Deactivated records live under the
     explicit Inactive filter. */
  const [statusFilter, setStatusFilter] = useState("current");
  const [chanFilter, setChanFilter] = useState<string | null>(null);
  const [groupFilter, setGroupFilter] = useState<string | null>(null);
  const [deactivating, setDeactivating] = useState<StaffMember | null>(null);
  const [deactivateBusy, setDeactivateBusy] = useState(false);
  const [deactivateError, setDeactivateError] = useState<string | null>(null);
  const [leaving, setLeaving] = useState<StaffMember | null>(null);
  const [leaveBusy, setLeaveBusy] = useState(false);
  const [leaveError, setLeaveError] = useState<string | null>(null);
  /* Two authorities (CC 2026-10-17 item B). Roster writes (New staff member, edit, duplicate, status)
     go through createStaff / updateStaff, which the server authorizes on staffing.write. The
     access-request queue is identity.team.manage, which the shell calls team.write. */
  const canWrite = can("staffing.write");
  const canManageAccess = can("team.write");

  const members = store.members;
  const groups  = store.groups;
  const rows = members.filter((m) => {
    if (statusFilter === "current" ? m.status === "inactive" : m.status !== statusFilter) return false;
    if (chanFilter && !(m.channels ?? []).includes(chanFilter as never)) return false;
    if (groupFilter && !(m.groups ?? []).includes(groupFilter)) return false;
    if (query.trim() && !`${staffName(m)} ${m.email} ${m.title} ${m.id}`.toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  });
  const active   = members.filter((m) => m.status === "active").length;
  const onLeave  = members.filter((m) => m.status === "on-leave").length;
  const inactive = members.filter((m) => m.status === "inactive").length;
  /* The platform's definition (CC 0370, option B; studio-staff.ts stats.bookable): an ACTIVE member whose
     channels name at least one of appointments, events or queues. Counted here from the whole roster the
     store already holds, by that same rule, so the tile moves at once when a member is deactivated or
     their channels change, without waiting for a re-read. */
  const bookable = members.filter((m) => m.status === "active" && (m.channels ?? []).length > 0).length;

  /* The server's duplicate (CC 0298): it blanks the email, turns sign-in off, drops the role, copies the
     services and audits it. The list shows the copy it returns; a null answer is a failure. */
  const dup = async (m: StaffMember) => { try { const copy = await store.duplicateMember(m.id); if (!copy) throw new Error("duplicateStaff returned no copy"); toast({ tone: "success", title: t("staff.list.staffDuplicated", "Staff duplicated"), subtitle: t("staff.list.copyIsInactive", "The copy starts inactive — find it under Inactive.") }); } catch (e) { console.warn("duplicate staff failed:", e); toast({ tone: "danger", title: t("staff.list.couldnTDuplicateStaff", "Couldn't duplicate staff member"), subtitle: t("staff.list.retryInAMoment", "Retry in a moment.") }); } };
  /* Deactivate = the soft-delete endpoint. The dialog stays open on failure, with
     the reason, so the user can retry without re-finding the row; nothing in the
     list moves until the server accepts the write. */
  const askDeactivate = (m: StaffMember) => { setDeactivateError(null); setDeactivating(m); };
  const deactivate = async () => {
    const m = deactivating;
    if (!m || deactivateBusy) return;
    setDeactivateBusy(true);
    setDeactivateError(null);
    try {
      await store.removeMember(m.id);
      setDeactivating(null);
      toast({ tone: "warn", title: t("staff.list.staffMemberDeactivated", "Staff member deactivated"), subtitle: formatICU(t("staff.list.staffMemberDeactivatedBody", "{name} moved to Inactive and their working hours were cleared. Booking and audit history are kept."), { name: staffName(m) }) });
    } catch (e) {
      console.warn("deactivate staff failed:", e);
      setDeactivateError(e instanceof Error && e.message ? e.message : t("staff.list.retryInAMoment", "Retry in a moment."));
    } finally {
      setDeactivateBusy(false);
    }
  };
  /* Status-only write (2026-10-08): `setMemberStatus` sends `{ status }` and
     nothing else. The whole-record save sent the cached locations too, and
     Reactivate wrote back the working hours the deactivation had cleared. */
  const setStatus = async (m: StaffMember, s: StaffMember["status"]) => { try { await store.setMemberStatus(m.id, s); toast({ tone: s === "active" ? "success" : "warn", title: i18nT("staff.list.markedStaffStatusesSLabelTolowercase.icu", "Marked {value0}", { value0: statusLabel(s).toLowerCase() }), ...(m.status === "inactive" && s === "active" ? { subtitle: t("staff.list.reactivatedSetHoursAgain", "Their working hours were cleared when they were deactivated. Set them again.") } : m.status === "on-leave" && s === "active" ? { subtitle: t("staff.list.backFromLeaveSetHoursAgain", "Their working hours were cleared when they went on leave. Set them again.") } : {}) }); } catch (e) { console.warn("update staff status failed:", e); toast({ tone: "danger", title: t("staff.list.couldnTUpdateStatus", "Couldn't update status"), subtitle: t("staff.list.retryInAMoment", "Retry in a moment.") }); } };
  /* Mark on leave clears working hours (2026-10-08). The status route clears
     the member's working rule for every status other than active — the booking
     engine offers anyone with an active rule, so keeping the hours would keep
     them bookable while away. Nothing here can keep both, so the action is
     confirmed first and says what it clears, the way Deactivate does. The
     dialog stays open on failure, with the reason, and nothing in the list
     moves until the server accepts the write. */
  const askLeave = (m: StaffMember) => { setLeaveError(null); setLeaving(m); };
  const markOnLeave = async () => {
    const m = leaving;
    if (!m || leaveBusy) return;
    setLeaveBusy(true);
    setLeaveError(null);
    try {
      await store.setMemberStatus(m.id, "on-leave");
      setLeaving(null);
      toast({ tone: "warn", title: i18nT("staff.list.markedStaffStatusesSLabelTolowercase.icu", "Marked {value0}", { value0: statusLabel("on-leave").toLowerCase() }), subtitle: formatICU(t("staff.list.markedOnLeaveBody", "{name}'s working hours were cleared. Booking and audit history are kept."), { name: staffName(m) }) });
    } catch (e) {
      console.warn("mark staff on leave failed:", e);
      setLeaveError(e instanceof Error && e.message ? e.message : t("staff.list.retryInAMoment", "Retry in a moment."));
    } finally {
      setLeaveBusy(false);
    }
  };
  const groupNames = (m: StaffMember) => (m.groups ?? []).map((g: string) => groups.find((x) => x.id === g)?.name).filter(Boolean) as string[];
  const anyFilter = statusFilter !== "current" || chanFilter || groupFilter || query.trim();
  /* The default view is empty only because everyone left is deactivated — say
     so, and offer the history view, instead of "no staff". */
  const onlyInactiveLeft = rows.length === 0 && statusFilter === "current" && !chanFilter && !groupFilter && !query.trim() && inactive > 0;
  const borderColor = (m: StaffMember) => m.status === "active" ? T.green1 : m.status === "on-leave" ? T.orange1 : T.n4;
  /* The line under a name: the title, else the role, else what kind of staff member (never the id). */
  const subtitle = (m: StaffMember) => m.title || (m.role ?? (eventsOnly ? t("staff.list.staffMemberFallback", "Staff member") : "Bookable staff"));

  const mkActions = (m: StaffMember) => ({
    onView: () => nav.go("staff.edit", { id: m.id, view: 1 }),
    viewLabel: "Preview",
    onEdit: canWrite ? () => nav.go("staff.edit", { id: m.id }) : undefined,
    onDuplicate: canWrite ? () => dup(m) : undefined,
    moreHeader: staffName(m),
    /* "Remove" said erase; the endpoint deactivates. One honest action replaces
       both it and "Mark inactive", which reached the same state. A deactivated
       record offers Reactivate instead. */
    moreItems: canWrite ? [
      ...(m.status !== "active"    ? [{ icon: <Icon.check size={14} />, label: m.status === "inactive" ? t("staff.list.reactivate", "Reactivate") : t("staff.list.markActive", "Mark active"), onClick: () => setStatus(m, "active") }]    : []),
      ...(m.status !== "on-leave" && m.status !== "inactive" ? [{ icon: <Icon.clock size={14} />, label: t("staff.list.markOnLeave", "Mark on leave"), onClick: () => askLeave(m) }]  : []),
      ...(m.status !== "inactive"  ? ["divider" as const, { icon: <Icon.lock size={14} />, label: t("staff.list.deactivate", "Deactivate…"), danger: true, onClick: () => askDeactivate(m) }] : []),
    ] : [],
  });

  /* Status filter — the shared pill-toggle group (was four hand-rolled pill
     Btns whose active state paired an inverting `T.p1` fill with fixed
     `T.white` ink). `staff.list.current` is a suggested key pending a catalog
     entry; the fallback renders the English verbatim until it lands. Every
     option carries its count so deactivated records stay discoverable. */
  const statusOptions = [
    { value: "current",  label: t("staff.list.current", "Current"),             count: firstLoad ? undefined : members.length - inactive },
    { value: "active",   label: t("common.status.active", "Active"),            count: firstLoad ? undefined : active },
    { value: "on-leave", label: t("staff.list.onLeave", "On leave"),            count: firstLoad ? undefined : onLeave },
    { value: "inactive", label: t("common.status.inactive", "Inactive"),        count: firstLoad ? undefined : inactive },
  ];

  /* Table columns (DS-convergence 2026-08-10) — the hand-rolled <table> is now
     the shared jrni-ui Table; row click/hover/keyboard activation and the
     per-row status accent are the primitive's. Actions stops row-nav. */
  const columns: TableColumn<StaffMember>[] = [
    { id: "name", header: t("staff.list.name", "Name"), cell: (m) => (
      <div style={{ display: "flex", alignItems: "center", gap: T.space[2] }}>
        <StaffAvatar m={m} size={32} />
        <div>
          <div style={{ color: T.n1, fontWeight: 600 }}>{staffName(m)}</div>
          <div style={{ color: T.soft, fontSize: T.text.label.fontSize }}>{subtitle(m)}</div>
        </div>
      </div>
    ) },
    { id: "role", header: t("staff.list.roleSignIn", "Role / sign-in"), cell: (m) => (
      m.signsIn
        ? <span style={{ display: "inline-flex", alignItems: "center", gap: T.space[2], color: T.p1, fontWeight: 600 }}><Icon.user size={13} /> {m.role}</span>
        : <span style={{ color: T.soft, display: "inline-flex", alignItems: "center", gap: T.space[2] }}><Icon.lock size={12} /> {t("staff.list.noSignIn", "No sign-in")}</span>
    ) },
    ...(eventsOnly ? [] : [{ id: "channels", header: t("staff.list.channels", "Channels"), cell: (m: StaffMember) => <ChannelGlyphs channels={m.channels} /> }]),
    { id: "groups", header: t("staff.list.groups", "Groups"), cell: (m) => <span style={{ color: T.n2 }}>{groupNames(m).join(", ") || "—"}</span> },
    { id: "locations", header: t("staff.list.locations", "Locations"), cell: (m) => <span style={{ color: (m.locations ?? []).length ? T.n1 : T.soft }}>{(m.locations ?? []).length || "—"}</span> },
    { id: "status", header: t("staff.list.status", "Status"), cell: (m) => <StaffStatusPill status={m.status} /> },
    { id: "actions", header: t("staff.list.actions", "Actions"), align: "right", minWidth: 130, cell: (m) => (
      <span style={{ display: "inline-flex" }} onClick={(e: MouseEvent<HTMLSpanElement>) => e.stopPropagation()}>
        <RowActions {...mkActions(m)} />
      </span>
    ) },
  ];

  return (
    <Screen rail="staff" tabs={sectionTabs(nav, store)} activeTab="staff">
      <PageHeader title={t("staff.list.staff", "Staff")}
        subtitle={eventsOnly ? t("staff.list.peopleWhoRunAndStaffYourEvents", "The people who run and staff your events — including those who never sign in.") : t("staff.list.thePeopleBookableToDeliverServicesThroughAppointmentsEventsAndQueuesIncl", "The people bookable to deliver services through appointments, events and queues — including those who never sign in.")}
        breadcrumb={null}
        actions={<>
          <ViewToggle mode={view} onChange={setView} />
          {canWrite ? <Btn icon={<Icon.plus />} onClick={() => nav.go("staff.edit", { new: 1 })}>{t("staff.list.newStaffMember", "New staff member")}</Btn> : null}
        </>}
      />
      <div style={{ padding: `${T.space[4]} ${T.space[6]} 0`, display: "grid", gridTemplateColumns: isMobile ? "repeat(2,1fr)" : `repeat(${eventsOnly ? 3 : 4},1fr)`, gap: T.space[3] }}>
        <StatTile label={t("staff.list.staff", "Staff")}    value={firstLoad ? <Skeleton width={40} height={22} /> : members.length} icon={<Icon.users size={14} />}    accent={T.p1} />
        <StatTile label={t("common.status.active", "Active")}   value={firstLoad ? <Skeleton width={40} height={22} /> : active}         icon={<Icon.check size={14} />}    accent={T.green1} />
        {/* The hint rides on the label (StatTileProps omits title on purpose). */}
          {eventsOnly ? null : <StatTile value={firstLoad ? <Skeleton width={40} height={22} /> : bookable} icon={<Icon.calendar size={14} />} accent={T.tDarkBlue}
            label={<span title={t("staff.list.bookableAnyChannelHint", "Active staff who take at least one of appointments, events or queues. The channel icons on each row show which.")}>{t("staff.list.bookableAnyChannel", "Bookable (any channel)")}</span>} />}
        <StatTile label={t("staff.list.groups", "Groups")}   value={firstLoad ? <Skeleton width={40} height={22} /> : groups.length}  icon={<Icon.tags size={14} />}     accent={T.p1} />
      </div>
      {/* Access-request queue (CC 0373), back on the Staff list 2026-10-16 (Pat) after
          `people` was removed. Same gate as before: team.write (the Invite gate). */}
      {canManageAccess ? <div style={{ padding: `${T.space[4]} ${T.space[6]} 0` }}><RegistrationRequests onApproved={() => store.reload()} /></div> : null}
      <FilterBar>
        <Input full={false} leading={<Icon.search size={14} />} placeholder={t("staff.list.searchNameEmailId", "Search name, email, ID…")} value={query} onChange={(e) => setQuery(e.target.value)} size="sm" style={{ width: isMobile ? "100%" : 240, maxWidth: "100%" }} />
        <FilterRule />
        <ChipToggleGroup ariaLabel={t("staff.list.status", "Status")} options={statusOptions} value={statusFilter} onChange={(v) => setStatusFilter(v as string)} />
        <FilterRule />
        {eventsOnly ? null : <FilterSelect label={chanFilter ? SVC_CHANNEL(chanFilter).label : t("staff.staffList.anyChannel", "Any channel")} active={!!chanFilter} anyLabel={t("staff.staffList.anyChannel", "Any channel")} options={SVC_CHANNELS.map((c) => c.label)} onPick={(lbl) => setChanFilter(SVC_CHANNELS.find((c) => c.label === lbl)?.id ?? null)} onClear={() => setChanFilter(null)} />}
        <FilterSelect label={groupFilter ? (groups.find((g) => g.id === groupFilter)?.name ?? t("staff.staffList.anyGroup", "Any group")) : t("staff.staffList.anyGroup", "Any group")} active={!!groupFilter} anyLabel={t("staff.staffList.anyGroup", "Any group")} options={groups.map((g) => g.name)} onPick={(name) => setGroupFilter(groups.find((g) => g.name === name)?.id ?? null)} onClear={() => setGroupFilter(null)} />
        {anyFilter ? <Btn variant="link" size="sm" onClick={() => { setStatusFilter("current"); setChanFilter(null); setGroupFilter(null); setQuery(""); }}>{t("staff.list.clearAll", "Clear all")}</Btn> : null}
      </FilterBar>

      <div style={listWrap(isMobile)}>
        <LoadState
          loading={store.loading}
          error={store.error}
          empty={rows.length === 0}
          onRetry={() => store.reload()}
          skeleton={shownView === "grid" ? "cards" : "table"}
          emptyState={onlyInactiveLeft ? (
            <EmptyState variant="card" icon={<Icon.users size={20} />} title={t("staff.list.noCurrentStaff", "No current staff")}
              body={formatICU(t("staff.list.onlyDeactivatedLeft", "Deactivated records ({count}) are kept under Inactive, with their history."), { count: inactive })}
              action={<Btn variant="secondary" onClick={() => setStatusFilter("inactive")}>{t("staff.list.showInactive", "Show inactive")}</Btn>} />
          ) : (
            <EmptyState variant="card" icon={<Icon.users size={20} />} title={t("staff.list.noStaffMatch", "No staff match")} body={t("staff.list.tryClearingAFilterOrAddAStaffMember", "Try clearing a filter, or add a staff member.")}
              action={canWrite ? <Btn icon={<Icon.plus size={14} />} onClick={() => nav.go("staff.edit", { new: 1 })}>{t("staff.list.newStaffMember", "New staff member")}</Btn> : null} />
          )}
        >
          {statusFilter === "inactive" ? (
            <Banner tone="info" size="compact" style={{ marginBottom: T.space[3] }}>
              {t("staff.list.inactiveExplainer", "Deactivated records keep their booking and audit history, but their working hours are cleared. Reactivate one from its row actions, then set its working hours again.")}
            </Banner>
          ) : null}
          {shownView === "grid" ? (
            <div style={{ display: "grid", gridTemplateColumns: CARD_GRID, gap: T.space[4] }}>
              {rows.map((m) => (
                <Card key={m.id} onClick={() => mkActions(m).onView()} style={{ padding: 0, cursor: "pointer", overflow: "hidden", display: "flex", flexDirection: "column", borderLeft: `3px solid ${borderColor(m)}` }}>
                  <div style={{ padding: T.space[4], flex: 1, display: "flex", flexDirection: "column", gap: T.space[3] }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: T.space[2] }}>
                      <StaffAvatar m={m} size={42} />
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: T.text.h3.fontSize, fontWeight: 700, color: T.n1 }}>{staffName(m)}</div>
                        <div style={{ fontSize: T.text.bodySm.fontSize, color: T.soft, marginTop: 1 /* optical */ }}>{subtitle(m)}</div>
                      </div>
                      <StaffStatusPill status={m.status} />
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: T.space[2], flexWrap: "wrap" }}>
                      {eventsOnly ? null : <ChannelGlyphs channels={m.channels} />}
                      {m.signsIn ? <span style={{ display: "inline-flex", alignItems: "center", gap: T.space[1], fontSize: T.text.label.fontSize, fontWeight: 600, color: T.p1 }}><Icon.user size={12} /> {m.role}</span> : <span style={{ fontSize: T.text.label.fontSize, color: T.soft, display: "inline-flex", alignItems: "center", gap: T.space[1] }}><Icon.lock size={11} /> {t("staff.list.noSignIn", "No sign-in")}</span>}
                    </div>
                    {groupNames(m).length ? <div style={{ display: "flex", flexWrap: "wrap", gap: T.space[2] }}>{groupNames(m).map((n) => <Pill key={n} tone="neutral">{n}</Pill>)}</div> : null}
                    <div style={{ marginTop: "auto", paddingTop: T.space[3], borderTop: `1px solid ${T.border}`, fontSize: T.text.label.fontSize, color: T.soft, display: "flex", gap: T.space[3] }}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: T.space[2] }}><Icon.loc size={12} /> {(m.locations ?? []).length} {t("staff.list.location", "location")}{(m.locations ?? []).length === 1 ? "" : t("staff.list.text", "s")}</span>
                    </div>
                  </div>
                  <CardActionBar {...mkActions(m)} />
                </Card>
              ))}
            </div>
          ) : (
            <Card style={{ padding: 0, minWidth: 0 }}>
              {/* Narrow widths scroll the table sideways rather than cut its last columns (item 4c). */}
              <div style={{ overflowX: "auto", minWidth: 0 }}>
              <Table<StaffMember>
                columns={columns}
                rows={rows}
                getRowId={(m) => m.id}
                onRowClick={(m) => mkActions(m).onView()}
                rowAccent={borderColor}
                style={{ border: "none", borderRadius: T.radius.none }}
              />
              </div>
            </Card>
          )}
        </LoadState>
        {!store.loading && !store.error && rows.length ? <FilterCount shown={rows.length} total={members.length} testid="staff-result-count" /> : null}
        <ExplainerFooter>{t("staff.list.a", "A")}{" "}<b>{t("staff.list.staffMember", "staff member")}</b> {eventsOnly ? t("staff.list.isTheSameObjectAsAUserEventsOnly", "is the same object as a user — some sign in (and carry a Studio role), others never sign in but can still staff your events. Authorization comes from their") : t("staff.list.isTheSameObjectAsAUserSomeSignInAndCarry", "is the same object as a user — some sign in (and carry a Studio role), others never sign in but are still bookable. Authorization comes from their")}{" "}<b>{t("staff.list.groups4ed379d4", "groups")}</b> {t("staff.list.plusIndividualGrants", "plus individual grants.")}</ExplainerFooter>
      </div>
      <ConfirmDialog open={!!deactivating} tone="warning" busy={deactivateBusy}
        title={formatICU(t("staff.list.deactivateTitle", "Deactivate {name}?"), { name: deactivating ? staffName(deactivating) : "" })}
        body={t("staff.list.deactivateBody", "Their roster record moves to Inactive and leaves the current staff list, and their working hours are cleared. Booking and audit history are kept. You can reactivate them from the Inactive filter, then set their working hours again.")}
        confirmLabel={t("staff.list.deactivateConfirm", "Deactivate")}
        cancelLabel={t("common.actions.cancel", "Cancel")}
        onCancel={() => { setDeactivating(null); setDeactivateError(null); }}
        onConfirm={() => void deactivate()}>
        {deactivateError ? (
          <Banner tone="danger" size="compact">
            {t("staff.list.couldntDeactivate", "Couldn't deactivate — nothing changed.")} {deactivateError}
          </Banner>
        ) : null}
      </ConfirmDialog>
      <ConfirmDialog open={!!leaving} tone="warning" busy={leaveBusy}
        title={formatICU(t("staff.list.markOnLeaveTitle", "Mark {name} on leave?"), { name: leaving ? staffName(leaving) : "" })}
        body={t("staff.list.markOnLeaveBody", "Their working hours are cleared, so they can't be booked while they're away. Booking and audit history are kept. When they're back, mark them active, then set their working hours again.")}
        confirmLabel={t("staff.list.markOnLeaveConfirm", "Mark on leave")}
        cancelLabel={t("common.actions.cancel", "Cancel")}
        onCancel={() => { setLeaving(null); setLeaveError(null); }}
        onConfirm={() => void markOnLeave()}>
        {leaveError ? (
          <Banner tone="danger" size="compact">
            {t("staff.list.couldntMarkOnLeave", "Couldn't mark on leave — nothing changed.")} {leaveError}
          </Banner>
        ) : null}
      </ConfirmDialog>
    </Screen>
  );
}

/* ═══ Tab 2: Staff groups ═══════════════════════════════════════════════ */
export function StaffGroups() {
  const t = useT();
  const eventsOnly = useEventsOnlyStaff();
  const nav = useNav();
  const store = useStaff();
  const [view, setView] = useViewMode("staffgroups:view");
  const isMobile = useIsMobile();
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  /* New group, edit, duplicate and publish are roster writes, authorized on staffing.write (CC 2026-10-17 item B). */
  const canWrite = can("staffing.write");

  const all = store.groups;
  const rows = all.filter((g) => {
    if (statusFilter !== "all" && g.status !== statusFilter) return false;
    if (query.trim() && !g.name.toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  });
  const published = all.filter((g) => g.status === "published").length;
  const countInGroup = (gid: string) => store.members.filter((m) => (m.groups ?? []).includes(gid)).length;
  const dup = async (g: StaffGroup) => { try { await store.upsertGroup({ ...g, id: "grp_" + Date.now(), name: g.name + " (copy)", status: "draft" }); toast({ tone: "success", title: t("staff.list.groupDuplicated", "Group duplicated") }); } catch (e) { console.warn("duplicate staff group failed:", e); toast({ tone: "danger", title: t("staff.list.couldnTDuplicateGroup", "Couldn't duplicate group"), subtitle: t("staff.list.retryInAMoment", "Retry in a moment.") }); } };
  const del = async (g: StaffGroup) => { try { await store.removeGroup(g.id); toast({ tone: "warn", title: t("staff.list.groupDeleted", "Group deleted") }); } catch (e) { console.warn("delete staff group failed:", e); toast({ tone: "danger", title: t("staff.list.couldnTDeleteGroup", "Couldn't delete group"), subtitle: t("staff.list.retryInAMoment", "Retry in a moment.") }); } };
  const toggle = async (g: StaffGroup) => { const next = g.status === "published" ? "draft" : "published"; try { await store.upsertGroup({ ...g, status: next }); toast({ tone: next === "published" ? "success" : "warn", title: next === "published" ? t("staff.list.groupPublished", "Group published") : t("common.status.movedToDraft", "Moved to draft") }); } catch (e) { console.warn("toggle staff group status failed:", e); toast({ tone: "danger", title: t("staff.list.couldnTUpdateGroup", "Couldn't update group"), subtitle: t("staff.list.retryInAMoment", "Retry in a moment.") }); } };

  const mkActions = (g: StaffGroup) => ({
    onView: () => nav.go("staff.group.edit", { id: g.id, view: 1 }),
    viewLabel: "Preview",
    onEdit: canWrite ? () => nav.go("staff.group.edit", { id: g.id }) : undefined,
    onDuplicate: canWrite ? () => dup(g) : undefined,
    moreHeader: g.name,
    moreItems: canWrite ? [
      g.status === "published" ? { icon: <Icon.refresh size={14} />, label: t("staff.list.moveToDraft", "Move to draft"), onClick: () => toggle(g) } : { icon: <Icon.check size={14} />, label: t("common.actions.publish", "Publish"), onClick: () => toggle(g) },
      "divider" as const,
      { icon: <Icon.trash size={14} />, label: t("staff.list.deleteGroup", "Delete group"), danger: true, onClick: () => del(g) },
    ] : [],
  });

  /* `staff.list.all` / `staff.list.drafts` are suggested keys pending catalog
     entries; the fallbacks render the shipped English verbatim until they land. */
  const statusOptions = [
    { value: "all",       label: t("staff.list.all", "All"),                      count: all.length },
    { value: "published", label: t("common.status.published", "Published"),       count: published },
    { value: "draft",     label: t("staff.list.drafts", "Drafts"),                count: all.length - published },
  ];

  const columns: TableColumn<StaffGroup>[] = [
    { id: "group", header: t("staff.list.group", "Group"), cell: (g) => (
      <div style={{ display: "flex", alignItems: "center", gap: T.space[2] }}>
        {/* The group's own hue is DATA (an arbitrary API colour), and no jrni-ui
            primitive renders a bare 14px colour swatch — kept as a plain span
            (not a raw form/table primitive, so no `ui-raw` marker applies). */}
        <span style={{ width: 14, height: 14, borderRadius: T.radius.sm, background: g.hue ?? T.p1, flexShrink: 0 }} />
        <div>
          <div style={{ color: T.n1, fontWeight: 600 }}>{g.name}</div>
          <div style={{ color: T.soft, fontSize: T.text.label.fontSize }}>{g.description}</div>
        </div>
      </div>
    ) },
    { id: "members", header: t("staff.list.members", "Members"), cell: (g) => <span style={{ color: T.n1 }}>{countInGroup(g.id)}</span> },
    { id: "status", header: t("staff.list.status", "Status"), cell: (g) => <GroupStatusPill status={g.status} /> },
    { id: "actions", header: t("staff.list.actions", "Actions"), align: "right", minWidth: 130, cell: (g) => (
      <span style={{ display: "inline-flex" }} onClick={(e: MouseEvent<HTMLSpanElement>) => e.stopPropagation()}>
        <RowActions {...mkActions(g)} />
      </span>
    ) },
  ];

  return (
    <Screen rail="staff" tabs={sectionTabs(nav, store)} activeTab="groups">
      <PageHeader title={t("staff.list.staffGroups", "Staff groups")} subtitle={eventsOnly ? t("staff.list.groupStaffByFunctionEventsOnly", "Group staff by function, so the right people are easy to find and put on an event.") : t("staff.list.groupStaffByFunctionAGroupSupportsASetOfServicesEvery", "Group staff by function. A group supports a set of services — every member inherits authorization to deliver them.")} breadcrumb={null}
        actions={<>
          <ViewToggle mode={view} onChange={setView} />
          {canWrite ? <Btn icon={<Icon.plus />} onClick={() => nav.go("staff.group.edit", { new: 1 })}>{t("staff.list.newGroup", "New group")}</Btn> : null}
        </>}
      />
      <FilterBar>
        <Input full={false} leading={<Icon.search size={14} />} placeholder={t("staff.list.searchGroups", "Search groups…")} value={query} onChange={(e) => setQuery(e.target.value)} size="sm" style={{ width: 240 }} />
        <FilterRule />
        <ChipToggleGroup ariaLabel={t("staff.list.status", "Status")} options={statusOptions} value={statusFilter} onChange={(v) => setStatusFilter(v as string)} />
      </FilterBar>
      <div style={listWrap(isMobile)}>
        <LoadState
          loading={store.loading}
          error={store.error}
          empty={rows.length === 0}
          onRetry={() => store.reload()}
          skeleton={view === "grid" || isMobile ? "cards" : "table"}
          emptyState={query.trim() || statusFilter !== "all" ? (
            <EmptyState variant="card" icon={<Icon.tags size={20} />} title={t("staff.list.noGroupsMatch", "No groups match")} body={t("staff.list.tryADifferentSearchOrCreateANewGroup", "Try a different search, or create a new group.")}
              action={canWrite ? <Btn icon={<Icon.plus size={14} />} onClick={() => nav.go("staff.group.edit", { new: 1 })}>{t("staff.list.newGroup", "New group")}</Btn> : null} />
          ) : (
            /* First use: nothing searched or filtered (CC 2026-10-19 walk 3 item 8). */
            <EmptyState variant="card" icon={<Icon.tags size={20} />} title={t("staff.list.noGroupsYet", "No groups yet")} body={t("staff.list.noGroupsYetBody", "Groups collect staff who share services or a schedule.")}
              action={canWrite ? <Btn icon={<Icon.plus size={14} />} onClick={() => nav.go("staff.group.edit", { new: 1 })}>{t("staff.list.newGroup", "New group")}</Btn> : null} />
          )}
        >
          {view === "grid" ? (
            <div style={{ display: "grid", gridTemplateColumns: CARD_GRID, gap: T.space[4] }}>
              {rows.map((g) => {
                const count = countInGroup(g.id);
                return (
                  <Card key={g.id} onClick={() => mkActions(g).onView()} style={{ padding: 0, cursor: "pointer", overflow: "hidden", display: "flex", flexDirection: "column", borderTop: `4px solid ${g.hue}` }}>
                    <div style={{ padding: T.space[4], flex: 1, display: "flex", flexDirection: "column", gap: T.space[3] }}>
                      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: T.space[2] }}>
                        <div style={{ minWidth: 0 }}><div style={{ fontSize: T.text.h3.fontSize, fontWeight: 700, color: T.n1 }}>{g.name || t("staff.list.text2", "Untitled")}</div><div style={{ fontSize: T.text.bodySm.fontSize, color: T.soft, marginTop: T.space[1], lineHeight: 1.4 }}>{g.description || t("staff.list.text3", "No description")}</div></div>
                        <GroupStatusPill status={g.status} />
                      </div>
                      <div style={{ marginTop: "auto", paddingTop: T.space[3], borderTop: `1px solid ${T.border}`, fontSize: T.text.label.fontSize, color: T.soft, display: "flex", gap: T.space[3] }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: T.space[2] }}><Icon.users size={12} /> {count} {t("staff.list.member", "member")}{" "}{count === 1 ? "" : t("staff.list.text", "s")}</span>
                        {eventsOnly ? null : <span style={{ display: "inline-flex", alignItems: "center", gap: T.space[2] }}><Icon.layers size={12} /> {(g.services ?? []).length} {t("staff.list.service", "service")}{(g.services ?? []).length === 1 ? "" : t("staff.list.text", "s")}</span>}
                      </div>
                    </div>
                    <CardActionBar {...mkActions(g)} />
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card style={{ padding: 0 }}>
              <Table<StaffGroup>
                columns={columns}
                rows={rows}
                getRowId={(g) => g.id}
                onRowClick={(g) => mkActions(g).onView()}
                style={{ border: "none", borderRadius: T.radius.none }}
              />
            </Card>
          )}
        </LoadState>
        {!store.loading && !store.error && rows.length ? <FilterCount shown={rows.length} total={all.length} testid="staff-groups-result-count" /> : null}
        {eventsOnly ? null : <ExplainerFooter>{t("staff.list.servicesAre", "Services are")}{" "}<b>{t("staff.list.assignedToGroups", "assigned to groups")}</b> {t("staff.list.everyMemberOfAGroupCanDeliverTheGroupSServicesIndividuals", "— every member of a group can deliver the group's services. Individuals can be granted extra services or excluded from specific ones in their profile.")}</ExplainerFooter>}
      </div>
    </Screen>
  );
}
